import {startTransition} from 'react'
import {CircleIcon, DotIcon} from '@primer/octicons-react'
import {BranchName, NavList} from '@primer/react'
import {useLocation} from '@github-ui/react-router'
import {getPullRequestTabSuffix} from '@github-ui/paths'
import {useFeatureState} from '@github-ui/pull-requests/use-feature-state'
import {FeatureState} from '@github-ui/pull-requests/page-data/payloads/header'

import styles from './StackList.module.css'
import iconStyles from './EntryStatusIcon.module.css'
import type {
  StackSectionEntryType,
  StackSectionStackEntry,
} from '../../../helpers/merge-box-status-calculator/stack-section-status'
import {getStackEntryLineClass, getBaseRefLineClass} from '../../../helpers/get-stack-entry-line-class'
import {getEffectiveBaseRefLineClass} from '../../../helpers/get-effective-entry-line-class'
import {useStackRebaseStatus} from '../../../hooks/mutations/use-stack-rebase-mutation'
import {useMergeabilityLiveUpdates} from '../../../hooks/use-mergeability-live-updates'
import {useStackMergeabilityQuery} from '../../../page-data/loaders/use-stack-mergeability-page-data'
import {useMergeMethodContext} from '../../../contexts/MergeMethodContext'
import type {AliveChannels as MergeBoxAliveChannels} from '../../../page-data/payloads/merge-box'
import type {AliveChannels as StatusChecksAliveChannels} from '../../../page-data/payloads/status-checks'
import {EntryStatusIcon} from './EntryStatusIcon'
import {EntryStatusLabel} from './EntryStatusLabel'
import {StackEntryNavItem} from './StackEntryNavItem'

export function StackList({
  entries,
  baseRefName,
  currentPullRequestId,
  stackEntries,
}: {
  entries: StackSectionEntryType[]
  baseRefName?: string
  currentPullRequestId: string
  stackEntries: StackSectionStackEntry[]
}) {
  const {displayStatus: rebaseDisplayState} = useStackRebaseStatus()
  const pullRequestStacksEffectiveEntryStatusesEnabled =
    useFeatureState('pullRequestStacksEffectiveEntryStatuses') === FeatureState.Enabled
  const {pathname} = useLocation()
  const tabSuffix = getPullRequestTabSuffix(pathname)

  if (pullRequestStacksEffectiveEntryStatusesEnabled) {
    return (
      <NavList className={styles.stackNavList} aria-label="Stacked pull requests list">
        {stackEntries.map((e, idx) => {
          const previousEntry = stackEntries[idx - 1]
          return (
            <StackEntryNavItem
              key={e.pull.number}
              entry={e}
              previousEntry={previousEntry}
              tabSuffix={tabSuffix}
              currentPullRequestId={currentPullRequestId}
            />
          )
        })}
        {baseRefName && stackEntries.length > 0 && (
          <li className={`${styles.branchItem} ${getEffectiveBaseRefLineClass(stackEntries)}`}>
            <span className={styles.branchItemContent}>
              <DotIcon size={16} className={`color-fg-muted ${iconStyles.alignedBaseRefDot}`} />
              <BranchName as="span">{baseRefName}</BranchName>
            </span>
          </li>
        )}
      </NavList>
    )
  }

  // TODO: Delete once pull_request_stacks_effective_entry_statuses_enabled is graduated
  return (
    <NavList className={styles.stackNavList} aria-label="Stacked pull requests list">
      {entries.map((e, idx) => {
        const previousEntry = entries[idx - 1]

        return (
          <NavList.Item
            key={e.pull.number}
            href={`${e.pull.url}${tabSuffix}`}
            aria-current={e.position === 'CURRENT'}
            className={getStackEntryLineClass(
              e,
              rebaseDisplayState[e.pull.number]?.state,
              previousEntry,
              previousEntry && rebaseDisplayState[previousEntry.pull.number]?.state,
            )}
          >
            <NavList.LeadingVisual>
              <EntryStatusIcon entry={e} rebaseState={rebaseDisplayState[e.pull.number]?.state} />
            </NavList.LeadingVisual>
            <span className={e.position === 'AFTER' ? 'fgColor-muted' : ''}>{e.pull.title}</span>
            <NavList.Description variant="block">
              <span className="fgColor-muted">
                #{e.pull.number} · {e.pull.headBranch}
              </span>
            </NavList.Description>
            <NavList.TrailingVisual>
              <EntryStatusLabel entry={e} rebaseState={rebaseDisplayState[e.pull.number]?.state} />
            </NavList.TrailingVisual>
            {!!e.channels && (
              <EntryMergeabilityUpdateListener
                basePageDataUrl={e.pull.url}
                currentPullRequestId={currentPullRequestId}
                {...e.channels}
              />
            )}
          </NavList.Item>
        )
      })}
      {baseRefName && entries.length > 0 && (
        <li className={`${styles.branchItem} ${getBaseRefLineClass(entries, rebaseDisplayState)}`}>
          <span className={styles.branchItemContent}>
            <CircleIcon size={12} className="fgColor-muted" />
            <BranchName as="span">{baseRefName}</BranchName>
          </span>
        </li>
      )}
    </NavList>
  )
}

function EntryMergeabilityUpdateListener({
  basePageDataUrl,
  currentPullRequestId,
  ...channels
}: {
  basePageDataUrl: string
  currentPullRequestId: string
} & MergeBoxAliveChannels &
  StatusChecksAliveChannels) {
  const {mergeMethod} = useMergeMethodContext()
  const {refetch} = useStackMergeabilityQuery({basePageDataUrl, currentPullRequestId, mergeMethod, enabled: false})
  useMergeabilityLiveUpdates({
    channels,
    refetchQuery: () => {
      startTransition(() => {
        refetch()
      })
    },
    throttleTime: 5000,
  })
  return null
}
